import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "@seller/app/locale";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { useSellerCustomersQuery } from "@/modules/customers/services/useSellerCustomersQuery";
import { CustomersTable } from "@/modules/customers/components/CustomersTable";
import { CustomerProfilePane } from "@/modules/customers/components/CustomerProfilePane";
import { MobileCustomersView } from "@/modules/customers/components/mobile/MobileCustomersView";
import { MobileCustomerFullScreen } from "@/modules/customers/components/mobile/MobileCustomerFullScreen";
import { useMinWidth } from "@/shared/hooks/useMinWidth";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 250;
// Desktop only keeps the master-detail split; tablet + phone use the
// single-column card list (→ full-screen profile), like the design's mobile.
const SPLIT_BREAKPOINT = 1024;

export function CustomersPage() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const t = useTranslations();
  const isSplit = useMinWidth(SPLIT_BREAKPOINT);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  // Phone: the open customer detail is URL-driven (`?customer=<id>`) so browser
  // back closes it and the slide-in replays per id — mirroring ProductsPage.
  const [searchParams, setSearchParams] = useSearchParams();
  const mobileCustomerId = searchParams.get("customer");
  const openMobileCustomer = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("customer", id);
    setSearchParams(next);
  };
  const closeMobileCustomer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next);
  };

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const { data, isLoading, error } = useSellerCustomersQuery({
    storeSlug,
    search: debouncedSearch.trim() ? debouncedSearch.trim() : undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const pageTitle = t(keys.customers.pageTitle);

  // Split (tablet + desktop): keep a customer selected so the profile pane is
  // never empty once results exist. On phones nothing shows until a tap.
  const desktopSelectedId =
    selectedCustomerId && items.some((c) => c.id === selectedCustomerId)
      ? selectedCustomerId
      : (items[0]?.id ?? null);

  const searchInput = (
    <input
      type="search"
      value={search}
      onChange={(event) => {
        setSearch(event.target.value);
        setOffset(0);
      }}
      placeholder={t(keys.customers.searchPlaceholder)}
      className="min-w-0 flex-1 bg-transparent text-sm text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
    />
  );

  const searchBox = (
    <div className="flex h-10 items-center gap-2.5 rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3">
      <SearchIcon />
      {searchInput}
    </div>
  );

  const listBody = isLoading && !data ? (
    <div className="p-4 text-sm text-[color:var(--text-muted)]">
      {t(keys.common.loading)}
    </div>
  ) : error ? (
    <div className="p-4 text-sm text-[color:var(--error)]">
      {t(keys.customers.loadError)}
    </div>
  ) : items.length === 0 ? (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <p className="text-base font-semibold text-[color:var(--text-primary)]">
        {t(keys.customers.emptyTitle)}
      </p>
      <p className="text-sm text-[color:var(--text-muted)]">
        {t(keys.customers.emptyBody)}
      </p>
    </div>
  ) : (
    <CustomersTable
      locale={locale}
      items={items}
      variant={isSplit ? "row" : "card"}
      selectedId={isSplit ? desktopSelectedId : null}
      onSelect={(customer) => setSelectedCustomerId(customer.id)}
    />
  );

  const pagination =
    totalPages > 1 ? (
      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          className="rounded-md border border-[color:var(--border-light)] px-3 py-1.5 text-sm text-[color:var(--text-secondary)] disabled:opacity-50"
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          disabled={offset === 0}
        >
          {t(keys.common.previous)}
        </button>
        <span className="text-xs text-[color:var(--text-muted)]">
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          className="rounded-md border border-[color:var(--border-light)] px-3 py-1.5 text-sm text-[color:var(--text-secondary)] disabled:opacity-50"
          onClick={() => setOffset(offset + PAGE_SIZE)}
          disabled={offset + PAGE_SIZE >= total}
        >
          {t(keys.common.next)}
        </button>
      </div>
    ) : null;

  // ── Tablet + desktop: master–detail split (list narrows on tablet) ─────────
  if (isSplit) {
    return (
      <div className="flex h-[calc(100dvh-4rem)] min-h-0">
        <aside className="flex w-[240px] shrink-0 flex-col border-e border-[color:var(--border-light)] bg-[color:var(--surface)] lg:w-[344px]">
          <div className="shrink-0 px-[18px] pb-3.5 pt-[18px]">
            <div className="mb-3.5">
              <h1 className="text-lg font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {pageTitle}
              </h1>
              <div className="mt-0.5 text-[12.5px] text-[color:var(--text-muted)]">
                {t(keys.customers.totalLabel, { count: total })}
              </div>
            </div>
            {searchBox}
          </div>
          <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-2.5 pb-3.5">
            {listBody}
          </div>
          {pagination}
        </aside>
        <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-[color:var(--canvas)]">
          <CustomerProfilePane
            locale={locale}
            storeSlug={storeSlug}
            customerId={desktopSelectedId}
          />
        </div>
      </div>
    );
  }

  // ── Phone: the redesigned Trioty mobile Customers screen + full-screen detail.
  // MobileCustomersView is self-contained (owns its own paging, filters and
  // display prefs), so it renders full-bleed like MobileProductsView. It has no
  // search of its own — the bottom-nav global search covers customers.
  return (
    <>
      <div className="flex flex-col px-4 pb-8">
        <MobileCustomersView
          storeSlug={storeSlug}
          locale={locale}
          onOpenCustomer={openMobileCustomer}
        />
      </div>

      <MobileCustomerFullScreen
        storeSlug={storeSlug}
        customerId={mobileCustomerId}
        locale={locale}
        onClose={closeMobileCustomer}
      />
    </>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4-4" />
    </svg>
  );
}
